import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Paper, Typography, Avatar, TextField, List, ListItem,
  ListItemAvatar, ListItemText, Divider, CircularProgress,
  IconButton, InputAdornment
} from '@mui/material';
import { Send as SendIcon, Search as SearchIcon } from '@mui/icons-material';
import { useAuth } from '../../store/authStore';
import {
  getUserConversations, subscribeToMessages, sendMessage, markMessagesAsRead
} from '../../api/messages';

const StudentMessages = () => {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const messagesEndRef = useRef(null);
  const unsubRef = useRef(null);

  useEffect(() => {
    loadConversations();
    return () => { if (unsubRef.current) unsubRef.current(); };
  }, [user?.id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadConversations = async () => {
    if (!user?.id) return;
    try {
      const convs = await getUserConversations(user.id);
      setConversations(convs);
    } catch (err) {
      console.error('Error loading conversations:', err);
    } finally {
      setLoading(false);
    }
  };

  const selectConversation = (conv) => {
    setSelectedConv(conv);
    if (unsubRef.current) unsubRef.current();
    unsubRef.current = subscribeToMessages(conv.id, (msgs) => {
      setMessages(msgs);
    });
    markMessagesAsRead(conv.id, user.id);
  };

  const handleSend = async () => {
    if (!newMessage.trim() || !selectedConv) return;
    try {
      await sendMessage(
        selectedConv.id,
        user.id,
        user.displayName || `${user.firstName} ${user.lastName}`,
        newMessage.trim()
      );
      setNewMessage('');
    } catch (err) {
      console.error('Error sending message:', err);
    }
  };

  const getOtherName = (conv) => {
    if (!conv.participantNames) return 'Unknown';
    const otherId = conv.participants?.find(id => id !== user.id);
    return conv.participantNames[otherId] || 'Unknown';
  };

  const getOtherInitial = (conv) => {
    return getOtherName(conv).charAt(0).toUpperCase();
  };

  const filteredConversations = conversations.filter(conv =>
    getOtherName(conv).toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatTime = (date) => {
    if (!date) return '';
    const d = date instanceof Date ? date : new Date(date);
    const now = new Date();
    const diffDays = Math.floor((now - d) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return d.toLocaleTimeString('en', { hour: '2-digit', minute: '2-digit' });
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return d.toLocaleDateString('en', { weekday: 'short' });
    return d.toLocaleDateString('en', { month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress sx={{ color: '#F5A623' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#FAFAFA', minHeight: '100vh', pt: 10, pb: 2 }}>
      <Box sx={{ maxWidth: 1100, mx: 'auto', px: 2 }}>
        <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>Messages</Typography>

        <Paper sx={{ display: 'flex', height: 'calc(100vh - 160px)', overflow: 'hidden', border: '1px solid #eee' }}>
          {/* Conversation List */}
          <Box sx={{ width: { xs: selectedConv ? 0 : '100%', md: 340 }, borderRight: '1px solid #eee', display: 'flex', flexDirection: 'column',
            transition: 'width 0.2s', overflow: 'hidden'
          }}>
            <Box sx={{ p: 2 }}>
              <TextField
                size="small" fullWidth placeholder="Search conversations..."
                value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#aaa' }} /></InputAdornment>
                }}
              />
            </Box>
            <Divider />
            <List sx={{ overflow: 'auto', flex: 1 }}>
              {filteredConversations.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6, px: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {conversations.length === 0
                      ? 'No messages yet. Send a message to a teacher from their profile!'
                      : 'No matching conversations'}
                  </Typography>
                </Box>
              ) : (
                filteredConversations.map(conv => (
                  <ListItem
                    key={conv.id}
                    button
                    selected={selectedConv?.id === conv.id}
                    onClick={() => selectConversation(conv)}
                    sx={{
                      py: 1.5, px: 2,
                      '&.Mui-selected': { bgcolor: '#FFF8F0', borderLeft: '3px solid #F5A623' },
                      '&:hover': { bgcolor: '#fafafa' }
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: '#F5A623', color: '#fff' }}>
                        {getOtherInitial(conv)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="subtitle2" fontWeight={600} noWrap>
                            {getOtherName(conv)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatTime(conv.lastMessageAt)}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 200 }}>
                          {conv.lastMessage || 'No messages yet'}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))
              )}
            </List>
          </Box>

          {/* Chat Area */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            {selectedConv ? (
              <>
                {/* Chat Header */}
                <Box sx={{ p: 2, borderBottom: '1px solid #eee', display: 'flex', alignItems: 'center', gap: 2, bgcolor: '#fff' }}>
                  {/* Back button on mobile */}
                  <IconButton
                    sx={{ display: { md: 'none' } }}
                    onClick={() => setSelectedConv(null)}
                  >
                    ←
                  </IconButton>
                  <Avatar sx={{ bgcolor: '#F5A623', color: '#fff' }}>
                    {getOtherInitial(selectedConv)}
                  </Avatar>
                  <Typography variant="h6" fontWeight={600}>
                    {getOtherName(selectedConv)}
                  </Typography>
                </Box>

                {/* Messages */}
                <Box sx={{ flex: 1, overflow: 'auto', p: 2, bgcolor: '#FAFAFA' }}>
                  {messages.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 6 }}>
                      <Typography color="text.secondary">No messages yet. Say hello!</Typography>
                    </Box>
                  )}
                  {messages.map(msg => (
                    <Box
                      key={msg.id}
                      sx={{
                        display: 'flex', mb: 1.5,
                        justifyContent: msg.senderId === user.id ? 'flex-end' : 'flex-start'
                      }}
                    >
                      <Box
                        sx={{
                          maxWidth: '70%', p: 1.5, borderRadius: 2.5,
                          bgcolor: msg.senderId === user.id ? '#F5A623' : '#fff',
                          color: msg.senderId === user.id ? '#fff' : '#1a1a1a',
                          border: msg.senderId === user.id ? 'none' : '1px solid #eee',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
                        }}
                      >
                        <Typography variant="body2">{msg.text}</Typography>
                        <Typography
                          variant="caption"
                          sx={{ opacity: 0.7, display: 'block', mt: 0.5, textAlign: 'right' }}
                        >
                          {msg.createdAt?.toLocaleTimeString?.('en', { hour: '2-digit', minute: '2-digit' }) || ''}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                  <div ref={messagesEndRef} />
                </Box>

                {/* Input */}
                <Box sx={{ p: 2, borderTop: '1px solid #eee', bgcolor: '#fff', display: 'flex', gap: 1 }}>
                  <TextField
                    fullWidth size="small" placeholder="Type a message..."
                    value={newMessage} onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
                    sx={{
                      '& .MuiOutlinedInput-root': { borderRadius: 3 }
                    }}
                  />
                  <IconButton
                    onClick={handleSend}
                    disabled={!newMessage.trim()}
                    sx={{
                      bgcolor: newMessage.trim() ? '#F5A623' : '#eee',
                      color: newMessage.trim() ? '#fff' : '#aaa',
                      '&:hover': { bgcolor: newMessage.trim() ? '#e09500' : '#ddd' },
                      borderRadius: 2, width: 42, height: 42
                    }}
                  >
                    <SendIcon fontSize="small" />
                  </IconButton>
                </Box>
              </>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, bgcolor: '#FAFAFA' }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    Select a conversation
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Choose a conversation from the left to start messaging
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default StudentMessages;
